import { useEffect, useState } from "react";
import { listVideoProviders } from "../lib/api";

/**
 * Single-source-of-truth dropdown for picking the image-to-video
 * provider on a job. Fetches the catalogue from
 * `GET /api/video-providers` on mount.
 *
 * Props:
 *  - value:        currently-selected provider id
 *  - onChange:     (newId) => void
 *  - testIdPrefix: data-testid prefix (default: "video-provider")
 *  - disabled:     bool
 */
export default function VideoProviderSelect({
  value,
  onChange,
  testIdPrefix = "video-provider",
  disabled = false,
}) {
  const [providers, setProviders] = useState([]);
  const [defaultId, setDefaultId] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await listVideoProviders();
        if (!alive) return;
        const list = Array.isArray(data?.providers) ? data.providers : [];
        setProviders(list);
        setDefaultId(data?.default || "");
        // If the parent hasn't picked yet, seed it with the server default.
        if (!value && data?.default && onChange) onChange(data.default);
      } catch {
        if (alive) setProviders([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selected = providers.find((p) => p.id === value)
    || providers.find((p) => p.id === defaultId)
    || providers[0]
    || null;

  return (
    <div className="space-y-1">
      <label
        htmlFor={`${testIdPrefix}-select`}
        className="block font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500"
      >
        VIDEO MODEL
      </label>
      <select
        id={`${testIdPrefix}-select`}
        data-testid={`${testIdPrefix}-select`}
        value={value || defaultId || ""}
        onChange={(e) => onChange?.(e.target.value)}
        disabled={disabled || loading}
        className="bg-black border border-zinc-800 focus:border-yellow-400/60 focus:outline-none rounded-sm px-2.5 py-1.5 text-xs text-zinc-100 min-w-[280px] disabled:opacity-50"
      >
        {loading && <option>Loading providers…</option>}
        {!loading && providers.length === 0 && (
          <option>No providers configured</option>
        )}
        {providers.map((p) => (
          <option
            key={p.id}
            value={p.id}
            disabled={!p.available}
          >
            {p.label}
            {p.cost_per_clip_usd ? ` · ~$${p.cost_per_clip_usd.toFixed(2)}/clip` : ""}
            {!p.available ? " (key missing)" : ""}
          </option>
        ))}
      </select>
      {selected && (
        <div
          data-testid={`${testIdPrefix}-hint`}
          className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500"
        >
          {selected.duration_sec}s clip ·{" "}
          <span className="text-yellow-300">
            ~${(selected.cost_per_clip_usd || 0).toFixed(2)}/clip
          </span>
          {" · "}~{Math.ceil(selected.expected_seconds_per_call / 60)} min/scene
        </div>
      )}
    </div>
  );
}
